/**
 * Client du Worker d'extraction (thread principal).
 *
 * Un seul Worker, réutilisé ; les requêtes sont corrélées par identifiant et
 * bornées dans le temps (60 s). L'interprétation XML (DOMParser) se fait ici :
 * un Worker n'y a pas accès.
 */
import type { ExtractionResult } from '../model';
import { extractFromXmlBytes, finalizePdfExtraction } from '../extract';
import type { ExtractionPayload } from './worker';

interface Pending {
  resolve(value: ExtractionResult): void;
  reject(reason: Error): void;
  timer: ReturnType<typeof setTimeout>;
  filename?: string;
}

export class ExtractionError extends Error {
  readonly code: string;

  constructor(message: string, code = 'UNKNOWN') {
    super(message);
    this.name = 'ExtractionError';
    this.code = code;
  }
}

const TIMEOUT_MS = 60_000;

let worker: Worker | null = null;
let sequence = 0;
const pending = new Map<number, Pending>();
const queue: Array<() => void> = [];

function settleNext(): void {
  queue.shift()?.();
}

function ensureWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module', name: 'facturelire-extraction' });
  worker.onmessage = (event: MessageEvent<{ id: number; ok: boolean; payload?: ExtractionPayload; error?: string; code?: string }>) => {
    const job = pending.get(event.data.id);
    if (!job) return;
    pending.delete(event.data.id);
    clearTimeout(job.timer);
    try {
      if (!event.data.ok || !event.data.payload) {
        throw new ExtractionError(event.data.error ?? 'Extraction impossible', event.data.code ?? 'UNKNOWN');
      }
      job.resolve(materialize(event.data.payload, job.filename));
    } catch (err) {
      job.reject(err instanceof Error ? err : new ExtractionError(String(err)));
    }
    settleNext();
  };
  worker.onerror = (event: ErrorEvent) => {
    const error = new ExtractionError(event.message || 'Worker en échec', 'WORKER_ERROR');
    for (const job of pending.values()) {
      clearTimeout(job.timer);
      job.reject(error);
    }
    pending.clear();
    queue.length = 0;
    worker?.terminate();
    worker = null;
  };
  return worker;
}

function materialize(payload: ExtractionPayload, filename?: string): ExtractionResult {
  if (payload.kind === 'pdf') {
    const attachments = payload.attachments.map((a) => ({
      filename: a.filename,
      bytes: new Uint8Array(a.buffer),
      subtype: a.subtype,
      source: a.source as 'name-tree' | 'af' | 'scan',
    }));
    return finalizePdfExtraction({ attachments, meta: payload.meta, warnings: payload.warnings }, { filename });
  }
  return extractFromXmlBytes(new Uint8Array(payload.buffer), { filename });
}

/** Extrait une facture depuis un fichier utilisateur (PDF ou XML). */
export async function extractInvoice(input: Blob | ArrayBuffer | Uint8Array, filename?: string): Promise<ExtractionResult> {
  const buffer = await toArrayBuffer(input);
  const w = ensureWorker();

  return new Promise<ExtractionResult>((resolve, reject) => {
    const run = (): void => {
      const id = ++sequence;
      const timer = setTimeout(() => {
        pending.delete(id);
        reject(new ExtractionError('Délai d’extraction dépassé (60 s)', 'TIMEOUT'));
        settleNext();
      }, TIMEOUT_MS);
      pending.set(id, { resolve, reject, timer, filename });
      w.postMessage({ id, bytes: buffer, filename }, [buffer]);
    };
    // Un seul document en cours de lecture : les gros PDF ne se concurrencent pas.
    if (pending.size === 0) run();
    else queue.push(run);
  });
}

async function toArrayBuffer(input: Blob | ArrayBuffer | Uint8Array): Promise<ArrayBuffer> {
  if (input instanceof ArrayBuffer) return input.slice(0);
  if (input instanceof Uint8Array) {
    const copy = new Uint8Array(input.byteLength);
    copy.set(input);
    return copy.buffer;
  }
  return input.arrayBuffer();
}

/** Libère le Worker (navigation, changement de page). */
export function disposeExtractionWorker(): void {
  worker?.terminate();
  worker = null;
  for (const job of pending.values()) {
    clearTimeout(job.timer);
    job.reject(new ExtractionError('Worker arrêté', 'DISPOSED'));
  }
  pending.clear();
  queue.length = 0;
}
